import { usStateOptions } from '@/utils/usStates';
import { usZipError } from '@/utils/numericFields';

/**
 * Field-level rules for a US mailing address. Shared by the patient edit modal and the
 * enrollment checklist so "valid" means the same thing in both places.
 */
export type AddressField = 'line1' | 'line2' | 'city' | 'state' | 'postalCode';

export interface AddressInput {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
}

export type AddressErrors = Partial<Record<AddressField, string>>;

export const ADDRESS_MAX_LENGTH = 128;

const STREET_CHARS = /^[A-Za-z0-9 .,'#&/()-]+$/;
const CITY_CHARS = /^[A-Za-z][A-Za-z .'-]*$/;

const STATE_CODES = new Set(usStateOptions.map((option) => option.value.toUpperCase()));
const STATE_BY_NAME = new Map(
  usStateOptions.map((option) => [option.label.toUpperCase(), option.value.toUpperCase()])
);

const clean = (value?: string | null) => (value ?? '').trim();

/** Two-letter code for a state given as a code or full name, or '' when it isn't a US state. */
export function normalizeUsState(value?: string | null): string {
  const raw = clean(value).toUpperCase();
  if (!raw) return '';
  if (STATE_CODES.has(raw)) return raw;
  return STATE_BY_NAME.get(raw) ?? '';
}

/** True when any part of the address has been entered. */
export function hasAnyAddressPart(address?: AddressInput | null): boolean {
  if (!address) return false;
  return (['line1', 'line2', 'city', 'state', 'postalCode'] as const).some((field) =>
    Boolean(clean(address[field]))
  );
}

/**
 * Error for one field. `required` means the address as a whole must be filled in: either the
 * caller requires it, or the user has started entering one (no half addresses).
 */
export function addressFieldError(
  field: AddressField,
  value: string | null | undefined,
  required: boolean
): string {
  const v = clean(value);

  if (field === 'line2') {
    if (!v) return '';
    if (v.length > ADDRESS_MAX_LENGTH) return `Address 2 cannot be longer than ${ADDRESS_MAX_LENGTH} characters.`;
    if (!STREET_CHARS.test(v)) return 'Address 2 has characters that are not allowed.';
    return '';
  }

  if (!v) {
    if (!required) return '';
    switch (field) {
      case 'line1':
        return 'Street address is required.';
      case 'city':
        return 'City is required.';
      case 'state':
        return 'State is required.';
      case 'postalCode':
        return 'ZIP code is required.';
    }
  }

  switch (field) {
    case 'line1':
      if (v.length > ADDRESS_MAX_LENGTH) return `Address 1 cannot be longer than ${ADDRESS_MAX_LENGTH} characters.`;
      if (!/[A-Za-z]/.test(v) || !/\d/.test(v)) return 'Enter a street address with a number and street name.';
      if (!STREET_CHARS.test(v)) return 'Address 1 has characters that are not allowed.';
      return '';
    case 'city':
      if (v.length > ADDRESS_MAX_LENGTH) return `City cannot be longer than ${ADDRESS_MAX_LENGTH} characters.`;
      if (!CITY_CHARS.test(v)) return 'City can only contain letters, spaces, periods, apostrophes and hyphens.';
      return '';
    case 'state':
      return normalizeUsState(v) ? '' : 'Select a US state.';
    case 'postalCode':
      return usZipError(v) ? 'Enter a 5-digit ZIP or ZIP+4 (12345-6789).' : '';
  }
  return '';
}

/** Errors for every field. Empty object means the address is valid. */
export function validateAddress(
  address: AddressInput | null | undefined,
  options: { required?: boolean } = {}
): AddressErrors {
  const required = Boolean(options.required) || hasAnyAddressPart(address);
  const errors: AddressErrors = {};
  for (const field of ['line1', 'line2', 'city', 'state', 'postalCode'] as const) {
    const message = addressFieldError(field, address?.[field], required);
    if (message) errors[field] = message;
  }
  return errors;
}

/** A complete, well-formed US address. */
export function isValidUsAddress(address?: AddressInput | null): boolean {
  return Object.keys(validateAddress(address, { required: true })).length === 0;
}
